import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Fab,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { CertificationSection } from "../components/cv/components/CertificationSection/CertificationSection";
import { EducationSection } from "../components/cv/components/EducationSection/EducationSection";
import { ExperienceSection } from "../components/cv/components/ExperienceSection/ExperienceSection";
import { LanguageSection } from "../components/cv/components/LanguageSection/LanguageSection";
import { NameAndPositionSection } from "../components/cv/components/NameAndPositionSection/NameAndPositionSection";
import { ProfessionalSummarySection } from "../components/cv/components/ProfessionalSummarySection/ProfessionalSummarySection";
import { SkillsSummarySection } from "../components/cv/components/SkillsSummarySection/SkillsSummarySection";
import AppTheme from "../components/shared-theme/AppTheme";
import Certification from "../types/Certification";
import Education from "../types/Education";
import Language from "../types/Language";
import ProfessionalAffiliation, { Project } from "../types/ProfessionalAffiliation";
import Skills from "../types/Skills";
import CVData from "../types/CVData";
import { useLocation, useNavigate } from "react-router-dom";
import { getCVById, saveCVData } from "../services/cvService";
import { useExportCV } from "../hooks/useExportCV";
import { useAuth } from "../hooks/useAuth";
import Position from "../types/Position";
import AllDefinitions from "../types/Definitions";
import {
  addNewCourse,
  addNewPosition,
  getAllDefinitions,
} from "../services/definitionsService";
import Skill from "../types/Skill";
import { useSkills } from "../hooks/useSkills";
import Course from "../types/Course";
import { WorkExperienceSidebar } from "../components/cv/components/ExperienceSection/components/WorkExperienceSidebar";

interface CreateCVProps {}

const normalizeSkills = (s: Partial<Skills> | undefined | null): Skills => ({
  backend: s?.backend ?? [],
  frontend: s?.frontend ?? [],
  devOps: s?.devOps ?? [],
  cloud: s?.cloud ?? [],
  database: s?.database ?? [],
  tools: s?.tools ?? [],
  others: s?.others ?? [],
});

export const CreateCV: React.FC<CreateCVProps> = (props: {
  disableCustomTheme?: boolean;
}) => {
  // Component state
  const [name, setName] = useState<string>("");
  const [position, setPosition] = useState<Position>({
    positionId: "",
    name: "",
    summary: "",
  });
  const [email, setEmail] = useState<string>("");
  const [professionalSummary, setProfessionalSummary] = useState<string>("");
  const [skills, setSkills] = useState<Skills>({
    backend: [],
    frontend: [],
    devOps: [],
    cloud: [],
    database: [],
    tools: [],
    others: [],
  });
  const [professionalAffiliations, setProfessionalAffiliations] = useState<
    ProfessionalAffiliation[]
  >([
    {
      employer: "",
      location: "",
      country: "",
      clientPeriod: "",
      projects: [
        {
          client: "",
          role: "",
          keyTechnologies: [],
          position: [""],
          projectDescription: "",
          projectPeriod: "",
          responsibilities: [""],
        },
      ],
    },
  ]);
  const [certifications, setCertifications] = useState<Certification[]>([
    {
      courseId: "",
      course: "",
      date: "",
      place: "",
    },
  ]);
  const [educations, setEducations] = useState<Education[]>([
    {
      degree: "",
      institution: "",
      period: "",
    },
  ]);

  const [languages, setLanguages] = useState<Language>({
    nativeLanguage: "",
    english: "",
    otherLanguages: [],
  });

  const [summaryFields, setSummaryFields] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState("Spanish");
  const [selectedLevel, setSelectedLevel] = useState("Basic");

  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
  const [allDefinitions, setAllDefinitions] = useState<AllDefinitions>({
    skills: [],
    positions: [],
    courses: [],
  });
  const [cvUId, setCvUId] = useState<string>("");
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [workExpSidebarOpen, setWorkExpSidebarOpen] = useState(false);
  const { addSkill: addSkillAPI } = useSkills();

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navigate = useNavigate();

  const steps = [
    "Personal Information",
    "Work Experience",
    "Education & Certifications",
    // "Additional Information",
  ];

  const location = useLocation();
  const { exportCVsByIds, exportCVsByIdsAsDocx } = useExportCV();
  const loggedInEmail = localStorage.getItem("email") ?? "";
  const userId = location.state?.user?.id ?? loggedInEmail;
  const { role } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setIsDataLoading(true);
      try {
        const [definitions, user] = await Promise.all([
          getAllDefinitions(),
          userId ? getCVById(userId).catch(() => null) : Promise.resolve(null),
        ]);

        if (cancelled) return;

        setAllDefinitions(definitions);

        const draftKey = `cv-draft-${userId}`;
        const draftJson = sessionStorage.getItem(draftKey);
        if (draftJson) {
          try {
            const draft = JSON.parse(draftJson);
            sessionStorage.removeItem(draftKey);
            setIsEditMode(draft.isEditMode ?? false);
            setCvUId(draft.cvUId ?? "");
            setName(draft.name ?? "");
            setEmail(draft.email ?? "");
            setPosition(draft.position ?? { positionId: "", name: "", summary: "" });
            setProfessionalSummary(draft.professionalSummary ?? "");
            setSkills(
              draft.skills ?? {
                backend: [],
                frontend: [],
                devOps: [],
                cloud: [],
                database: [],
                tools: [],
                others: [],
              }
            );
            setProfessionalAffiliations(
              draft.professionalAffiliations ?? [
                {
                  employer: "",
                  location: "",
                  country: "",
                  clientPeriod: "",
                  projects: [{
                    client: "",
                    role: "",
                    keyTechnologies: [],
                    position: [""],
                    projectDescription: "",
                    projectPeriod: "",
                    responsibilities: [""],
                  }],
                },
              ]
            );
            setCertifications(
              draft.certifications ?? [
                { courseId: "", course: "", date: "", place: "" },
              ]
            );
            setEducations(
              draft.education ?? [
                { degree: "", institution: "", period: "" },
              ]
            );
            setLanguages(
              draft.languages ?? {
                nativeLanguage: "",
                english: "",
                otherLanguages: [],
              }
            );
            setSummaryFields(draft.summaryFields ?? []);
            setSelectedLanguage(draft.selectedLanguage ?? "Spanish");
            setSelectedLevel(draft.selectedLevel ?? "Basic");
            setActiveStep(draft.activeStep ?? 0);
          } catch {
            // Invalid draft, fall through to normal load
          }
        } else if (user) {
          setIsEditMode(true);
          setCvUId(user.cvUId);
          setName(user.name);
          setPosition(user.position);
          setProfessionalSummary(user.professionalSummary);
          setSkills(
            user.skills || {
              backend: [],
              frontend: [],
              devOps: [],
              cloud: [],
              database: [],
              tools: [],
              others: [],
            }
          );
          setProfessionalAffiliations(
            (user.professionalAffiliations || []).map(
              (aff: ProfessionalAffiliation) => ({
                employer: aff.employer || "",
                location: aff.location || "",
                country: aff.country || "",
                clientPeriod: aff.clientPeriod || "",
                projects: aff.projects.map((project: Project) => ({
                  client: project.client || "",
                  role: project.role || "",
                  keyTechnologies: project.keyTechnologies || [],
                  position: project.position || [""],
                  projectDescription: project.projectDescription || "",
                  projectPeriod: project.projectPeriod || "",
                  responsibilities: project.responsibilities || [""],
                })),
              })
            )
          );
          setCertifications(
            user.certifications || [
              { course: "", place: "", date: "", courseId: "" },
            ]
          );
          setEducations(
            user.education || [
              { degree: "", institution: "", period: "" },
            ]
          );
          setLanguages(
            user.languages || {
              nativeLanguage: "",
              english: "",
              otherLanguages: [],
            }
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      } finally {
        if (!cancelled) {
          setIsDataLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const validateStep = (currentStep: number) => {
    const newErrors: Record<string, string> = {};
    if (currentStep === 0) {
      if (!name.trim()) {
        newErrors.name = "Name is required.";
      }

      if (!position.name.trim()) {
        newErrors.position = "Position is required.";
      }
      if (!professionalSummary.trim()) {
        newErrors.professionalSummary = "Professional Summary is required.";
      }
      if (
        skills.backend.length === 0 &&
        skills.frontend.length === 0 &&
        skills.devOps.length === 0 &&
        skills.cloud.length === 0 &&
        skills.tools.length === 0 &&
        skills.others.length === 0
      ) {
        newErrors.skills = "You must provide some of your skills.";
      }
    }

    if (currentStep === 1) {
      professionalAffiliations.forEach((affiliation, affIndex) => {
        if (!affiliation.employer.trim()) {
          newErrors[`affiliations[${affIndex}].employer`] =
            "Employer is required.";
        }
        if (!affiliation.location.trim()) {
          newErrors[`affiliations[${affIndex}].location`] =
            "Location is required.";
        }
        if (!affiliation.country.trim()) {
          newErrors[`affiliations[${affIndex}].country`] =
            "Country is required.";
        }
        if (!affiliation.clientPeriod.trim()) {
          newErrors[`affiliations[${affIndex}].clientPeriod`] =
            "Client period is required.";
        }
        affiliation.projects.forEach((project, projIndex) => {
          const isInternship = project.position.includes("Internship");

          if (!isInternship) {
            if (!project.client.trim()) {
              newErrors[
                `affiliations[${affIndex}].projects[${projIndex}].client`
              ] = "Client/Project is required.";
            }
            if (!project.role.trim()) {
              newErrors[
                `affiliations[${affIndex}].projects[${projIndex}].role`
              ] = "Role is required.";
            }
            if (!project.projectDescription.trim()) {
              newErrors[
                `affiliations[${affIndex}].projects[${projIndex}].projectDescription`
              ] = "Project description is required.";
            }
            if (!project.projectPeriod.trim()) {
              newErrors[
                `affiliations[${affIndex}].projects[${projIndex}].projectPeriod`
              ] = "Project period is required.";
            }
            if (project.position.length === 0) {
              newErrors[
                `affiliations[${affIndex}].projects[${projIndex}].position`
              ] = "At least one position is required.";
            }
          }

          if (project.keyTechnologies.length === 0) {
            newErrors[
              `affiliations[${affIndex}].projects[${projIndex}].keyTechnologies`
            ] = "At least one key technology is required.";
          }
          if (project.responsibilities[0].trim() === "") {
            newErrors[
              `affiliations[${affIndex}].projects[${projIndex}].responsibilities`
            ] = "At least one responsibility is required.";
          }
        });
      });
    }

    if (currentStep === 2) {
      educations.forEach((item, index) => {
        if (!item.degree.trim()) {
          newErrors[`educations[${index}].degree`] = "Degree is requiered.";
        }
        if (!item.institution.trim()) {
          newErrors[`educations[${index}].institution`] =
            "Institution is requiered.";
        }
        if (!item.period.trim()) {
          newErrors[`educations[${index}].period`] = "Period is requiered.";
        }
      });
      if (!languages.nativeLanguage.trim()) {
        newErrors.nativeLanguage = "Native language is required.";
      }
      if (!languages.english.trim()) {
        newErrors.english = "You must select a level.";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const removeErrorMessage = (field: string) => {
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (updatedErrors[field]) {
        delete updatedErrors[field];
      }
      return updatedErrors;
    });
  };

  const handleAddLanguage = () => {
    const newLanguage = {
      name: selectedLanguage,
      level: selectedLevel,
    };

    const languageExists = languages.otherLanguages.some(
      (lang) => lang.name === newLanguage.name
    );

    if (!languageExists) {
      setLanguages((prevLanguages) => ({
        ...prevLanguages,
        otherLanguages: [...prevLanguages.otherLanguages, newLanguage],
      }));
    }
  };

  const handleCreateNewSkill = async (name: string, type: Skill["type"]) => {
    // Create on backend
    const created = await addSkillAPI(name, type as any);
    // Reflect in local definitions so options include it immediately
    setAllDefinitions((prev: any) => ({
      ...prev,
      skills: [...prev.skills, created],
    }));
    return created;
  };

  const handleCreateNewPosition = async (title: string): Promise<Position> => {
    const payload: Position = {
      positionId: "",
      name: title.trim(),
      summary: "",
    };
    const res = await addNewPosition(payload);

    const saved: Position = {
      positionId: res.position.positionId,
      name: res.position.name,
      summary: res.position.summary ?? "",
    };

    // make it immediately available in all dropdowns/autocompletes
    setAllDefinitions((prev) => ({
      ...prev,
      positions: [...prev.positions, saved],
    }));
    return saved;
  };

  const handleCreateNewCourse = async (title: string): Promise<Course> => {
    const res = await addNewCourse({ courseId: "", course: title.trim() });
    const saved: Course = {
      courseId: res.course.courseId,
      course: res.course.course,
    };
    setAllDefinitions((prev) => ({
      ...prev,
      courses: [...prev.courses, saved],
    }));
    return saved;
  };

  const handleDeleteLanguage = (index: number) => {
    setLanguages((prevLanguages) => {
      const updatedLanguages = [...prevLanguages.otherLanguages];
      updatedLanguages.splice(index, 1);
      return {
        ...prevLanguages,
        otherLanguages: updatedLanguages,
      };
    });
  };

  const handleExperienceInput = (
    event: { target: { name: string; value: any } },
    index: number,
    projectIndex?: number
  ) => {
    const { name, value } = event.target;
    setProfessionalAffiliations((prev) =>
      prev.map((affiliation, idx) => {
        if (idx !== index) return affiliation;

        if (projectIndex !== undefined) {
          const updatedProjects = affiliation.projects.map((project, pIdx) =>
            pIdx === projectIndex ? { ...project, [name]: value } : project
          );
          return { ...affiliation, projects: updatedProjects };
        }
        return { ...affiliation, [name]: value };
      })
    );
  };

  const handleCertification = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { name, value } = e.target;
    setCertifications((prevCertifications) => {
      const updatedCertifications = [...prevCertifications];

      if (name === "course") {
        const selectedCourse = allDefinitions.courses.find(
          (course) => course.courseId === value
        );

        if (selectedCourse) {
          updatedCertifications[index] = {
            ...updatedCertifications[index],
            courseId: selectedCourse.courseId,
            course: selectedCourse.course,
          };
        }
      } else {
        updatedCertifications[index] = {
          ...updatedCertifications[index],
          [name]: value,
        };
      }
      return updatedCertifications;
    });
  };
  const handleEducation = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { name, value } = e.target;
    setEducations((prevEducations) => {
      const updatedEducations = [...prevEducations];
      updatedEducations[index] = {
        ...updatedEducations[index],
        [name]: value,
      };
      return updatedEducations;
    });
  };

  const addAffiliation = () => {
    setProfessionalAffiliations((prevAffiliations) => [
      ...prevAffiliations,
      {
        employer: "",
        location: "",
        country: "",
        clientPeriod: "",
        projects: [],
      },
    ]);
  };

  const addProject = (affiliationIndex: number) => {
    setProfessionalAffiliations((prevAffiliations) =>
      prevAffiliations.map((affiliation, index) =>
        index === affiliationIndex
          ? {
              ...affiliation,
              projects: [
                ...affiliation.projects,
                {
                  position: [""],
                  client: "",
                  role: "",
                  projectPeriod: "",
                  projectDescription: "",
                  responsibilities: [""],
                  keyTechnologies: [],
                },
              ],
            }
          : affiliation
      )
    );
  };

  const removeProject = (affiliationIndex: number, projectIndex: number) => {
    setProfessionalAffiliations((prevAffiliations) =>
      prevAffiliations.map((affiliation, index) =>
        index === affiliationIndex
          ? {
              ...affiliation,
              projects: affiliation.projects.filter(
                (_, idx) => idx !== projectIndex
              ),
            }
          : affiliation
      )
    );
  };

  const removeAffiliation = (affiliationIndex: number) => {
    setProfessionalAffiliations((prevAffiliations) =>
      prevAffiliations.filter((_, index) => index !== affiliationIndex)
    );
  };

  const addCertification = () => {
    setCertifications((prevCertifications) => [
      ...prevCertifications,
      {
        courseId: "",
        course: "",
        date: "",
        place: "",
      },
    ]);
  };

  const removeCertification = (index: number) => {
    setCertifications((prevCertifications) =>
      prevCertifications.filter((_, idx) => idx !== index)
    );
  };

  const addEducatuon = () => {
    setEducations((prevEducations) => [
      ...prevEducations,
      {
        degree: "",
        institution: "",
        period: "",
      },
    ]);
  };

  const removeEducation = (index: number) => {
    setEducations((prevEducations) =>
      prevEducations.filter((_, idx) => idx !== index)
    );
  };

  const handleSaveAndDownload = async (format: "pdf" | "docx") => {
    if (validateStep(activeStep)) {
      setIsExporting(true);

      const userData: CVData = {
        cvUId,
        cvId: userId,
        name,
        position,
        professionalSummary,
        skills,
        professionalAffiliations,
        certifications,
        education: educations,
        languages,
        status: "active",
        type: "employee",
      };

      try {
        await saveCVData(userData, {
          sendEmailOnCreate: !isEditMode,
          notifyEmail: email || loggedInEmail || undefined,
        });
        sessionStorage.removeItem(getDraftKey());

        if (format === "pdf") {
          await exportCVsByIds([userData.cvId], true);
        } else {
          await exportCVsByIdsAsDocx([userData.cvId], true);
        }
      } catch (error) {
        console.error("Error saving CV:", error);
        alert("Failed to save the CV. Please try again.");
      } finally {
        setIsExporting(false);
      }
    }
  };

  const handleSaveOnly = async () => {
    if (validateStep(activeStep)) {
      const userData: CVData = {
        cvUId,
        cvId: userId,
        name,
        position,
        professionalSummary,
        skills,
        professionalAffiliations,
        certifications,
        education: educations,
        languages,
        status: "active",
        type: "employee",
      };

      try {
        await saveCVData(userData, {
          sendEmailOnCreate: !isEditMode,
          notifyEmail: email || loggedInEmail || undefined,
        });
        sessionStorage.removeItem(getDraftKey());
        setSaveSnackbarOpen(true); // show success message
      } catch (error) {
        console.error("Error saving CV:", error);
      }
    }
  };

  const getDraftKey = () => `cv-draft-${userId}`;

  const handlePreviewCV = () => {
    const previewData: CVData = {
      cvUId,
      cvId: userId,
      name,
      position,
      professionalSummary,
      skills,
      professionalAffiliations,
      certifications,
      education: educations,
      languages,
      status: "active",
      type: "employee",
    };
    const draft = {
      ...previewData,
      email,
      summaryFields,
      selectedLanguage,
      selectedLevel,
      activeStep,
      isEditMode,
    };
    sessionStorage.setItem(getDraftKey(), JSON.stringify(draft));
    navigate("/pdf-viewer", { state: { userId, formData: previewData } });
  };

  const addField = () => {
    setSummaryFields([...summaryFields, ""]);
  };

  const removeField = (index: number) => {
    const newFields = summaryFields.filter((_, i) => i !== index);
    setSummaryFields(newFields);
  };

  const handleUpdateSectionField = (value: string, index: number) => {
    const updatedFields = [...summaryFields];
    updatedFields[index] = value;
    setSummaryFields(updatedFields);
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ maxWidth: "70%", margin: "auto", padding: "7vh 0 100px 0" }}>
        {isDataLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
            }}
          >
            <CircularProgress size={48} />
          </Box>
        ) : (
          <>
        <Typography variant="h2" textAlign="center" mb={"2rem"}>
          {isEditMode ? "Edit CV" : "Create CV"}
        </Typography>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: activeStep === 0 ? "flex" : "none",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <NameAndPositionSection
                name={name}
                setName={setName}
                position={position}
                setPosition={setPosition}
                email={email}
                setEmail={setEmail}
                setProfessionalSummary={setProfessionalSummary}
                errors={errors}
                removeErrorMessage={removeErrorMessage}
                isEditMode={isEditMode}
                role={role}
                positions={allDefinitions.positions}
                addPosition={handleCreateNewPosition}
              />
              <ProfessionalSummarySection
                professionalSummary={professionalSummary}
                summaryFields={summaryFields}
                setProfessionalSummary={setProfessionalSummary}
                addField={addField}
                removeField={removeField}
                handleUpdate={handleUpdateSectionField}
                error={errors.professionalSummary}
                removeErrorMessage={removeErrorMessage}
              />
              <SkillsSummarySection
                skills={normalizeSkills(skills)}
                setSkills={setSkills}
                error={errors.skills}
                removeErrorMessage={removeErrorMessage}
                allSkills={allDefinitions.skills || []}
                onCreateNewSkill={handleCreateNewSkill}
              />
          </Box>

          <Box
            sx={{
              display: activeStep === 1 ? "block" : "none",
            }}
          >
            <WorkExperienceSidebar
              affiliations={professionalAffiliations}
              visible={workExpSidebarOpen}
              onToggle={() => setWorkExpSidebarOpen((v) => !v)}
            />
            <Box>
              <Typography variant="h4">Professional Affiliations</Typography>
              {professionalAffiliations.map((affiliation, index) => (
                <ExperienceSection
                  key={index}
                  affiliation={affiliation}
                  index={index}
                  handleInput={handleExperienceInput}
                  addProject={addProject}
                  removeProject={removeProject}
                  removeAffiliation={removeAffiliation}
                  errors={errors}
                  removeErrorMessage={removeErrorMessage}
                  positions={allDefinitions.positions}
                  skills={allDefinitions.skills}
                />
              ))}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addAffiliation}
                fullWidth
              >
                Add Another Affiliation
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: activeStep === 2 ? "block" : "none" }}>
              <Typography variant="h4">Certifications</Typography>
              {certifications.map((certification, index) => (
                <CertificationSection
                  key={index}
                  certification={certification}
                  index={index}
                  courses={allDefinitions.courses}
                  onCreateNewCourse={handleCreateNewCourse}
                  handleCertification={handleCertification}
                  removeCertification={removeCertification}
                />
              ))}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addCertification}
                fullWidth
                sx={{ marginTop: "1rem" }}
              >
                Add Another Certification
              </Button>
              <Typography variant="h4" sx={{ mt: 2 }}>
                Education
              </Typography>
              {educations.map((education, index) => (
                <EducationSection
                  key={index}
                  education={education}
                  index={index}
                  handleEducation={handleEducation}
                  removeEducation={removeEducation}
                  errors={errors}
                  removeErrorMessage={removeErrorMessage}
                />
              ))}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addEducatuon}
                fullWidth
                sx={{ marginTop: "1rem" }}
              >
                Add Another Education
              </Button>
              <Box sx={{ marginTop: "1rem" }}>
                <LanguageSection
                  handleAddLanguage={handleAddLanguage}
                  handleDeleteLanguage={handleDeleteLanguage}
                  languages={languages}
                  selectedLanguage={selectedLanguage}
                  selectedLevel={selectedLevel}
                  setLanguages={setLanguages}
                  setSelectedLanguage={setSelectedLanguage}
                  setSelectedLevel={setSelectedLevel}
                  errors={errors}
                  removeErrorMessage={removeErrorMessage}
                />
              </Box>
          </Box>

        </Box>
          </>
        )}
      </Box>

      {!isDataLoading && (
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 3,
          py: 2,
          bgcolor: "background.paper",
          boxShadow: 3,
          borderTop: 1,
          borderColor: "divider",
          zIndex: 1100,
        }}
      >
        <Box sx={{ maxWidth: "70%", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        {activeStep === steps.length - 1 && (
          <Box sx={{ display: "flex", gap: "5px" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePreviewCV}
            >
              Preview CV
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSaveAndDownload("pdf")}
              disabled={isExporting}
            >
              {isExporting ? (
                <CircularProgress size={24} color="info" />
              ) : isEditMode ? (
                "Save & Export PDF"
              ) : (
                "Create & Export PDF"
              )}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleSaveAndDownload("docx")}
              disabled={isExporting}
            >
              {isExporting ? (
                <CircularProgress size={24} color="info" />
              ) : isEditMode ? (
                "Save & Export Word"
              ) : (
                "Create & Export Word"
              )}
            </Button>
            {isEditMode && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveOnly}
              >
                Save Changes
              </Button>
            )}
          </Box>
        )}
        {activeStep !== steps.length - 1 && (
          <Button variant="contained" color="primary" onClick={handleNext}>
            Next
          </Button>
        )}
        </Box>
      </Box>
      )}

      {!isDataLoading && showScrollTop && (
        <Tooltip title="Scroll to top" placement="left">
          <Fab
            size="medium"
            aria-label="Scroll to top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            sx={(theme) => ({
              position: "fixed",
              bottom: 88,
              right: 24,
              zIndex: 1099,
              backgroundColor: theme.palette.grey[800],
              color: theme.palette.grey[50],
              "&:hover": { backgroundColor: theme.palette.grey[700] },
            })}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        </Tooltip>
      )}

      <Snackbar
        open={saveSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSaveSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Changes saved successfully!
        </Alert>
      </Snackbar>
    </AppTheme>
  );
};
